import React, { FC, useState, useEffect } from 'react';
import styles from './roomstay-calculator.module.scss';

import classNames from 'classnames';
import ClippedBox from '../roomstay-blog-image-block/clipped-box';
import { useWindowSize } from 'usehooks-ts';
import ButtonBookADemo from '../ButtonBookADemo';
import Button from '@/components/button';

export interface CalculatorProps {
    title?: string;
    description?: string;
    subTitle?: string;
    highlightText?: string;
    subDescription?: string;
    ctaButton?: {
        text?: string;
        href?: string;
    };
}

interface CalculatorState {
    directBookings: string;
    visitors: string;
    adr: string;
    los: string;
}

interface CalculatorResults {
    convRateCurrent: number;
    convRateProposed: number;
    directBookingsProposed: number;
    revenueCurrent: number;
    revenueProposed: number;
    otaCurrent: number;
    otaProposed: number;
    otaSavingsMonthly?: number;
}

// Add error state interface
interface ErrorState {
    directBookings: boolean;
    visitors: boolean;
    adr: boolean;
    los: boolean;
}

const RoomStayCalculator: FC<CalculatorProps> = (props) => {
    const { title, description, subTitle, highlightText, subDescription, ctaButton } = props;
    const { width: windowWidth } = useWindowSize();
    const isTablet = windowWidth <= 991;

    const [calculatorState, setCalculatorState] = useState<CalculatorState>({
        directBookings: '',
        visitors: '',
        adr: '',
        los: '',
    });
    const [showPopup, setShowPopup] = useState({ value: false, time: Date.now() });

    // Add error state
    const [errorState, setErrorState] = useState<ErrorState>({
        directBookings: false,
        visitors: false,
        adr: false,
        los: false,
    });

    const [results, setResults] = useState<CalculatorResults>({
        convRateCurrent: 0,
        convRateProposed: 6,
        directBookingsProposed: 0,
        revenueCurrent: 0,
        revenueProposed: 0,
        otaCurrent: 0,
        otaProposed: 0,
        otaSavingsMonthly: 0,
    }); 

    const getNumericValue = (value: string): number => {
        const parsed = parseFloat(value);
        return isNaN(parsed) ? 0 : parsed;
    };

    const calculateResults = (state: CalculatorState): CalculatorResults => {
        const directBookings = getNumericValue(state.directBookings);
        const visitors = getNumericValue(state.visitors);
        const adr = getNumericValue(state.adr);
        const los = getNumericValue(state.los);
        
        const otaRate = 0.15; // fixed 15% OTA commission
        const convRateProposed = 6; // fixed benchmark
        
        const convRateCurrent = visitors > 0 ? (directBookings / visitors) * 100 : 0;
        
        // Proposed direct bookings with 6% conversion rate
        const directBookingsProposed = visitors * (convRateProposed / 100);
        
        // Revenue: Last month direct bookings x ADR x Average Length of Stay
        const revenueCurrent = directBookings * adr * los;
        const revenueProposed = directBookingsProposed * adr * los;
        
        // OTA Fees (what would be paid if bookings went through OTA)
        const otaCurrent = revenueCurrent * otaRate;
        const otaProposed = revenueProposed * otaRate;
        
        // Additional revenue from increased direct bookings (avoiding OTA commissions)
        const additionalRevenueMonthly = revenueProposed - revenueCurrent;
        
        return {
            convRateCurrent,
            convRateProposed,
            directBookingsProposed,
            revenueCurrent,
            revenueProposed,
            otaCurrent,
            otaProposed
        };
    };

    const onCalculateFinalResult = () => {
        const finalResult = results.revenueProposed - results.revenueCurrent;
        if (finalResult > 0) {
            setShowPopup({ value: false, time: Date.now() });
            setResults({...results, otaSavingsMonthly: finalResult});
        } else if (finalResult === 0) {
            // Check which inputs are empty or zero and set error state
            const newErrorState: ErrorState = {
                directBookings: !calculatorState.directBookings || getNumericValue(calculatorState.directBookings) === 0,
                visitors: !calculatorState.visitors || getNumericValue(calculatorState.visitors) === 0,
                adr: !calculatorState.adr || getNumericValue(calculatorState.adr) === 0,
                los: !calculatorState.los || getNumericValue(calculatorState.los) === 0,
            };
            
            setErrorState(newErrorState);
            setShowPopup({ value: false, time: Date.now() });
        } else {
            setShowPopup({ value: true, time: Date.now() });
        }
    };

    useEffect(() => {
        const newResults = calculateResults(calculatorState);
        setResults({...newResults});
    }, [calculatorState]);

    const handleInputChange = (field: keyof CalculatorState, value: string) => {
        const sanitizedValue = value.replace(/[^0-9.]/g, '');
        
        const parts = sanitizedValue.split('.');
        const finalValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : sanitizedValue;
        
        const newState = {
            ...calculatorState,
            [field]: finalValue,
        };

        setCalculatorState(newState);

        // Clear error state for this field when user enters a value
        if (finalValue && getNumericValue(finalValue) > 0) {
            setErrorState(prev => ({
                ...prev,
                [field]: false
            }));
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === '-' || e.key === '+') {
            e.preventDefault();
        }
        
        const allowedKeys = [
            'Backspace', 'Delete', 'Tab', 'Escape', 'Enter', '.', 'ArrowLeft', 'ArrowRight',
            '0', '1', '2', '3', '4', '5', '6', '7', '8', '9'
        ];
        
        // if (!e.ctrlKey && !e.metaKey) {
        //     e.preventDefault();
        // }
    };

    const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
        // Prevent scroll from changing number input values
        e.currentTarget.blur();
    };

    const formatCurrency = (value: number): string => {
        return value.toLocaleString(undefined, { style: "currency", currency: "AUD" });
    };

    const formatNumber = (value: number): string => {
        return value.toLocaleString();
    };

    const formatPercentage = (value: number): string => {
        return value.toFixed(2) + '%';
    };

    const displayNumberValue = (calculatedValue: number): string => {
        if (isNaN(calculatedValue)) return '0';
        return formatNumber(calculatedValue);
    };

    const displayCurrencyValue = (calculatedValue: number): string => {
        if (isNaN(calculatedValue)) return 'A$0';
        return formatCurrency(calculatedValue);
    };

    const displayPercentageValue = (calculatedValue: number): string => {
        if (isNaN(calculatedValue)) return '0.00%';
        return formatPercentage(calculatedValue);
    };

    return (
        <div className={styles.container}>
            <div className={styles.wrapperBox}>
                <div className={styles.boxTop}>
                    <div className={styles.backgroundColorTop}></div>
                    <h2 className={styles.title} dangerouslySetInnerHTML={{ __html: title || '' }}></h2>
                    <div className={styles.description} dangerouslySetInnerHTML={{ __html: description || '' }}></div>
                </div>

                <div className={styles.boxCenter}>
                    <div className={styles.backgroundColorCenter}>
                        <ClippedBox
                            configs={[]}
                            customPolygon="0% 0%, 100% 0%, 100% 100%, 0% 100%"
                            className={classNames(styles.backgroundColor, styles.backgroundClippedTop)}
                            style={{
                                backgroundColor: '#00D7E1',
                                maxHeight: isTablet ? 120 : 100,
                                transform: 'rotate(180deg)',
                                height: '100%',
                            }}
                        />
                        <ClippedBox
                            configs={[]}
                            customPolygon="0% 0%, 100% 0%, 0% 100%, 0% 0%"
                            className={classNames(styles.backgroundColor, styles.backgroundClippedBottom)}
                            style={{
                                backgroundColor: '#00D7E1',
                                maxHeight: isTablet ? 48 : 100,
                                height: '100%',
                            }}
                        />
                    </div>

                    <div className={styles.calculatorTable}>
                        <div className={styles.calculatorTableRow}>
                            <div className={`${styles.calculatorTableCell} ${styles.hiddenOnMobile}`}>Metrics</div>
                            <div className={styles.calculatorTableCellWrapper}>
                                <div className={`${styles.calculatorTableCell} ${styles.calculatorTableCellHeader}`}>
                                    Current 
                                </div>
                                <div className={`${styles.calculatorTableCell} ${styles.calculatorTableCellHeader}`}>
                                    RoomStay
                                    <span>(6% Sample Conversion Rate)</span>
                                </div>
                            </div>
                        </div>

                        <div className={styles.calculatorTableRow}>
                            <div className={`${styles.calculatorTableCell} ${styles.fontWeightRegular}`}>
                                Last Month Direct Bookings
                            </div>
                            <div className={styles.calculatorTableCellWrapper}>
                                <div className={`${styles.calculatorTableCell}`}>
                                    <input 
                                        type="number"
                                        min={0} 
                                        value={calculatorState.directBookings}
                                        onChange={(e) => handleInputChange('directBookings', e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        onWheel={handleWheel}
                                        placeholder="Enter booking numbers"
                                        className={classNames({
                                            [styles.inputError]: errorState.directBookings
                                        })}
                                    />
                                </div>
                                <div className={`${styles.calculatorTableCell} ${styles.calculatorTableCellBody}`}>
                                    {displayNumberValue(results.directBookingsProposed)}
                                </div>
                            </div>
                        </div>

                        <div className={styles.calculatorTableRow}>
                            <div className={`${styles.calculatorTableCell} ${styles.fontWeightRegular}`}>
                                Last Month Website Visitors
                            </div>
                            <div className={styles.calculatorTableCellWrapper}>
                                <div className={`${styles.calculatorTableCell}`}>
                                    <input 
                                        type="number"
                                        min={0} 
                                        value={calculatorState.visitors}
                                        onChange={(e) => handleInputChange('visitors', e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        onWheel={handleWheel}
                                        placeholder="Enter Visitor numbers"
                                        className={classNames({
                                            [styles.inputError]: errorState.visitors
                                        })}
                                    />
                                </div>
                                <div className={`${styles.calculatorTableCell} ${styles.calculatorTableCellBody}`}>
                                    {displayNumberValue(getNumericValue(calculatorState.visitors))}
                                </div>
                            </div>
                        </div>

                        <div className={styles.calculatorTableRow}>
                            <div className={`${styles.calculatorTableCell} ${styles.fontWeightRegular}`}>
                                Conversion Rate 
                                <span className={styles.calculatorTableCellSubtitle}> (%)</span>
                            </div>
                            <div className={styles.calculatorTableCellWrapper}>
                                <div className={`${styles.calculatorTableCell} ${styles.calculatorTableCellBody} ${styles.fontWeightRegular}`}>
                                    {displayPercentageValue(results.convRateCurrent)}
                                </div>
                                <div className={`${styles.calculatorTableCell} ${styles.calculatorTableCellBody}`}>
                                    {formatPercentage(results.convRateProposed)}
                                </div>
                            </div>
                        </div>

                        <div className={styles.calculatorTableRow}>
                            <div className={`${styles.calculatorTableCell} ${styles.fontWeightRegular}`}>
                                ADR 
                                <span className={styles.calculatorTableCellSubtitle}> (Average Daily Rate)</span>
                            </div>
                            <div className={styles.calculatorTableCellWrapper}>
                                <div className={styles.calculatorTableCell}>
                                    <input 
                                        type="number"
                                        min={0}
                                        value={calculatorState.adr}
                                        onChange={(e) => handleInputChange('adr', e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        onWheel={handleWheel}
                                        placeholder="Enter ADR numbers"
                                        className={classNames({
                                            [styles.inputError]: errorState.adr
                                        })}
                                    />
                                </div>
                                <div className={`${styles.calculatorTableCell} ${styles.calculatorTableCellBody}`}>
                                    {displayNumberValue(getNumericValue(calculatorState.adr))}
                                </div>
                            </div>
                        </div>

                        <div className={styles.calculatorTableRow}>
                            <div className={`${styles.calculatorTableCell} ${styles.fontWeightRegular}`}>Average Length of Stay <span className={styles.calculatorTableCellSubtitle}> (Nights)</span></div>
                            <div className={styles.calculatorTableCellWrapper}>
                                <div className={styles.calculatorTableCell}>
                                    <input 
                                        type="number"
                                        min={0} 
                                        value={calculatorState.los}
                                        onChange={(e) => handleInputChange('los', e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        onWheel={handleWheel}
                                        placeholder="Enter LOS numbers"
                                        className={classNames({
                                            [styles.inputError]: errorState.los
                                        })}
                                    />
                                </div>
                                <div className={`${styles.calculatorTableCell} ${styles.calculatorTableCellBody}`}>
                                    {displayNumberValue(getNumericValue(calculatorState.los))}
                                </div>
                            </div>
                        </div>

                        <div className={`${styles.calculatorTableRow}`}>
                            <div className={`${styles.calculatorTableCell} ${styles.fontWeightRegular}`}>Estimated Direct Booking Revenue</div>
                            <div className={styles.calculatorTableCellWrapper}>
                                <div className={`${styles.calculatorTableCell} ${styles.calculatorTableCellBody} ${styles.fontWeightRegular}`}>
                                    {displayCurrencyValue(results.revenueCurrent)}
                                </div>
                                <div className={`${styles.calculatorTableCell} ${styles.calculatorTableCellBody}`}>
                                    {displayCurrencyValue(results.revenueProposed)}
                                </div>
                            </div>
                        </div>

                        {/* <div className={styles.calculatorTableRow}>
                            <div className={`${styles.calculatorTableCell} ${styles.fontWeightRegular}`}>OTA Fees if via OTA 
                                <span className={styles.calculatorTableCellSubtitle}> (15% Comm.)</span>
                            </div>
                            <div className={styles.calculatorTableCellWrapper}>
                                <div className={`${styles.calculatorTableCell} ${styles.calculatorTableCellBody} ${styles.fontWeightRegular}`}>
                                    {displayCurrencyValue(results.otaCurrent)}
                                </div>
                                <div className={`${styles.calculatorTableCell} ${styles.calculatorTableCellBody}`}>
                                    {displayCurrencyValue(results.otaProposed)}
                                </div>
                            </div>
                        </div> */}

                        <div>
                            <Button type="secondary" size="large" htmlType="button" onClick={onCalculateFinalResult} className={styles.unactive}>
                                Calculate Results
                            </Button>
                        </div>

                        <div className={`${styles.profitBlock}`}>
                            <div className={styles.profitItem}>
                                <div className={styles.profitItemTitle}>
                                    Monthly Estimated Direct Booking Revenue Growth with Roomstay
                                </div>
                                <div className={styles.profitItemContent} id="finalResult" data-condition={JSON.stringify(showPopup)}>
                                    {displayCurrencyValue(results?.otaSavingsMonthly || 0)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.boxBottom}>
                    <div className={styles.backgroundColorBottom}></div>

                    <h2 className={styles.subTitle} dangerouslySetInnerHTML={{ __html: subTitle || '' }}></h2>
                    <div className={styles.highlightText} dangerouslySetInnerHTML={{ __html: highlightText || '' }}></div>
                    <div className={styles.subDescription} dangerouslySetInnerHTML={{ __html: subDescription || '' }}></div>

                    {ctaButton?.text && (
                        <ButtonBookADemo className={styles.button} icon={false} iconFillColor={false} type="highty" size="default" htmlType="button" link={ctaButton?.href}>
                            {ctaButton?.text || 'Book a Demo'}
                        </ButtonBookADemo>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RoomStayCalculator;

